import type { CellClassParams, ColGroupDef, ICellRendererParams } from "ag-grid-community";
import { Maximize2, RefreshCw } from "lucide-react";

import CellRenderer from "../../Sheet/CellRenderer";
import type {
  VersionDiffStatus,
  VersionPreviewColumn,
  VersionPreviewColumnConfigTarget,
  VersionPreviewRow,
} from "../../types";
import { getDiffCellStyle } from "../../utils";

import { VersionInlineCellDiffViewer } from "./VersionCellDiffViewer";
import VersionPreviewHeader, { VersionPreviewGroupHeader } from "./VersionPreviewHeader";

import type { SheetColumn, SheetRowData } from "@/components/Sheet";
import type { ColumnType, SmartColumn } from "@/queries/smart-tables/types";
import { resolveColumnType } from "@/routes/smart-table/utils/compositionColumns";
import {
  mapMetricGroupColumnDefs,
  nestSmartTableGroupedChildColumns,
  type GroupableColumn,
} from "@/routes/smart-table/utils/smartTableGroupedColumns";
import { buildSmartTableBaseColumnDefs } from "@/routes/smart-table/utils/smartTableBaseColumnDefs";
import type { SmartTableColumnDef } from "@/routes/smart-table/utils/smartTableColumnGroups";
import { groupConsecutiveInputColumns } from "@/routes/smart-table/utils/smartTableColumnGroups";

export type PreviewColumnDef = SmartTableColumnDef<VersionPreviewRow>;
type PreviewLeafColumnDef = SheetColumn<VersionPreviewRow>;
type PreviewGroupColumnDef = ColGroupDef<VersionPreviewRow>;

type OpenCellDialog = (rowIndex: number, columnId: string) => void;
type OpenColumnConfig = (target: VersionPreviewColumnConfigTarget) => void;
type OpenColumnAnalytics = (columnId: string, columnTitle: string) => void;
type VersionPreviewCellRendererParams = ICellRendererParams<VersionPreviewRow> & {
  openDialog?: OpenCellDialog;
};

interface PreviewCellDiffMetadata {
  beforePreview?: string;
  afterPreview?: string;
  beforeValue?: string;
  afterValue?: string;
}

interface VersionPreviewColumnDefOptions {
  onOpenColumnConfig?: OpenColumnConfig;
  selectedColumnId?: string | null;
  onOpenColumnAnalytics?: OpenColumnAnalytics;
}

const PREVIEW_UPDATED_DIFF_STYLE = {
  backgroundColor: "rgba(59, 130, 246, 0.055)",
} as const;

const getPreviewDiffCellStyle = (
  status?: VersionDiffStatus,
): { backgroundColor: string } | undefined => {
  if (status === "updated") {
    return PREVIEW_UPDATED_DIFF_STYLE;
  }

  return getDiffCellStyle(status);
};

const PREVIEW_COLUMN_DEFAULTS = {
  editableColumn: false,
  sortable: false,
  resizable: true,
  minWidth: 140,
} as const;

export const PreviewRowNumberHeader = () => (
  <span className="text-[11px] font-medium tabular-nums text-muted-foreground/40">#</span>
);

const createPreviewCellStyle =
  (column: VersionPreviewColumn) =>
  (params: CellClassParams<VersionPreviewRow>): Record<string, string | number> | null => {
    const cellDiff = params.data?.[`_diff_${column.id}`] as VersionDiffStatus | undefined;
    const isManualCellUpdate =
      cellDiff === "updated" && Boolean(params.data?.[`_diff_meta_${column.id}`]);
    const baseStyle = isManualCellUpdate
      ? {}
      : (getPreviewDiffCellStyle(cellDiff ?? column.diffStatus) ?? {});
    const isExecutionTouched = Boolean(params.data?.[`_execution_touched_${column.id}`]);

    if (!isExecutionTouched) {
      return baseStyle;
    }

    return {
      borderLeft: "3px solid rgba(59, 130, 246, 0.55)",
    };
  };

const VersionPreviewCellRenderer = ({
  colDef,
  data,
  openDialog,
  ...params
}: VersionPreviewCellRendererParams) => {
  const cellRendererParams = { ...params, colDef, data, openDialog };
  const columnId = colDef?.colId ?? colDef?.field;
  const diffStatus = columnId
    ? (data?.[`_diff_${columnId}`] as VersionDiffStatus | undefined)
    : undefined;
  const diffMetadata = columnId
    ? (data?.[`_diff_meta_${columnId}`] as PreviewCellDiffMetadata | null | undefined)
    : undefined;
  const isExecutionTouched = columnId ? Boolean(data?.[`_execution_touched_${columnId}`]) : false;

  const beforeDiffValue = diffMetadata?.beforeValue ?? diffMetadata?.beforePreview ?? "";
  const afterDiffValue = diffMetadata?.afterValue ?? diffMetadata?.afterPreview ?? "";
  const showInlineDiff =
    diffStatus === "updated" && Boolean(diffMetadata) && beforeDiffValue !== afterDiffValue;
  const showRefreshMarker = diffStatus === "updated" && isExecutionTouched && !showInlineDiff;
  const rowIndex = data?._rowIndex;
  const canOpenCellViewer = typeof rowIndex === "number" && typeof columnId === "string";

  if (showInlineDiff) {
    return (
      <div
        className="group/cell relative h-full w-full text-left"
        title={`${diffMetadata?.beforePreview ?? ""} → ${diffMetadata?.afterPreview ?? ""}`}
      >
        <VersionInlineCellDiffViewer oldValue={beforeDiffValue} newValue={afterDiffValue} />
        <button
          type="button"
          className="absolute right-0 top-1/2 hidden h-5 w-5 -translate-y-1/2 items-center justify-center rounded-sm bg-background/90 text-muted-foreground shadow-sm backdrop-blur-sm hover:text-foreground group-hover/cell:flex"
          aria-label="Open full cell view"
          onClick={event => {
            event.stopPropagation();
            if (canOpenCellViewer) {
              openDialog?.(rowIndex, columnId);
            }
          }}
          onMouseDown={event => event.stopPropagation()}
          onPointerDown={event => event.stopPropagation()}
          tabIndex={-1}
        >
          <Maximize2 className="h-3 w-3" />
        </button>
      </div>
    );
  }

  if (showRefreshMarker) {
    return (
      <div className="flex h-full w-full min-w-0 items-center gap-1.5">
        <RefreshCw className="h-3 w-3 flex-shrink-0 text-blue-500/80" strokeWidth={1.8} />
        <div className="h-full min-w-0 flex-1">
          <CellRenderer {...cellRendererParams} />
        </div>
      </div>
    );
  }

  return <CellRenderer {...cellRendererParams} />;
};

const isPreviewSidebarSelected = (field: string, selectedColumnId?: string | null): boolean => {
  if (!selectedColumnId) {
    return false;
  }

  return field === selectedColumnId || field.startsWith(`${selectedColumnId}_`);
};

const createPreviewLeafColumn = ({
  column,
  colId,
  field,
  headerName,
  columnType,
  config,
  openDialog,
  cellStyle,
  valueGetter,
  columnGroupShow,
  parentColumnId,
  compositionSource,
  onOpenColumnConfig,
  onOpenColumnAnalytics,
  selectedColumnId,
}: {
  column: VersionPreviewColumn;
  colId: string;
  field?: string;
  headerName: string;
  columnType?: ColumnType;
  config?: Record<string, unknown> | null;
  openDialog: OpenCellDialog;
  onOpenColumnConfig?: OpenColumnConfig;
  onOpenColumnAnalytics?: OpenColumnAnalytics;
  selectedColumnId?: string | null;
  cellStyle: SheetColumn<VersionPreviewRow>["cellStyle"];
  valueGetter?: SheetColumn<VersionPreviewRow>["valueGetter"];
  columnGroupShow?: "open" | "closed";
  parentColumnId?: string;
  compositionSource?: string;
}): SheetColumn<VersionPreviewRow> => ({
  colId,
  ...(valueGetter ? { valueGetter } : { field: field ?? colId }),
  header: headerName,
  headerName,
  headerComponent: VersionPreviewHeader,
  headerComponentParams: {
    columnType,
    config: config ?? null,
    title: headerName,
    diffStatus: column.diffStatus,
    columnId: colId,
    columnTitle: headerName,
    parentColumnId,
    compositionSource,
    onOpenColumnConfig,
    onOpenColumnAnalytics: parentColumnId ? undefined : onOpenColumnAnalytics,
  },
  ...(columnGroupShow ? { columnGroupShow } : {}),
  ...PREVIEW_COLUMN_DEFAULTS,
  headerClass: () =>
    isPreviewSidebarSelected(colId, selectedColumnId) ? "smart-table-header-selected" : "",
  headerStyle: getPreviewDiffCellStyle(column.diffStatus),
  cellRenderer: VersionPreviewCellRenderer,
  cellRendererParams: {
    columnType,
    openDialog,
    parentColumnId,
    compositionSource,
  },
  cellStyle,
  cellClassRules: {
    "smart-table-col-selected": (params: { colDef: { field?: string; colId?: string } }) =>
      isPreviewSidebarSelected(
        params.colDef.field || params.colDef.colId || colId,
        selectedColumnId,
      ),
  },
});

const getPreviewColumnType = (column: PreviewColumnDef): string | null => {
  if ("children" in column) {
    return null;
  }

  const headerParams = column.headerComponentParams as { columnType?: unknown } | undefined;
  return resolveColumnType(headerParams?.columnType) ?? null;
};

const withPreviewGroupHeaders = <TColumn extends GroupableColumn, TRow extends SheetRowData>(
  columns: readonly TColumn[],
  columnDefs: readonly SmartTableColumnDef<TRow>[],
  getExtraHeaderParams?: (column: TColumn) => Record<string, unknown>,
): SmartTableColumnDef<TRow>[] =>
  mapMetricGroupColumnDefs(columns, columnDefs, (columnDef, parentColumn) => ({
    ...columnDef,
    headerGroupComponent: VersionPreviewGroupHeader,
    headerGroupComponentParams: {
      columnType: parentColumn.type,
      config: parentColumn.config ?? null,
      title: parentColumn.title,
      columnId: parentColumn.id,
      columnTitle: parentColumn.title,
      ...getExtraHeaderParams?.(parentColumn),
    },
  }));

export const createReadOnlyPreviewColumnDefs = <TRow extends SheetRowData>(
  columns: readonly SmartColumn[],
  {
    tableId,
    sheetId,
    openDialog,
    promptTemplateColumnType,
  }: {
    tableId: string;
    sheetId: string;
    openDialog: OpenCellDialog;
    promptTemplateColumnType: string;
  },
): SmartTableColumnDef<TRow>[] =>
  withPreviewGroupHeaders(
    columns,
    nestSmartTableGroupedChildColumns({
      columns,
      columnDefs: buildSmartTableBaseColumnDefs<
        SmartColumn,
        SheetColumn<TRow>,
        ColGroupDef<TRow>,
        { data?: TRow }
      >(columns, {
        createLeafColumnDef: ({
          column,
          colId,
          field,
          headerName,
          columnType,
          valueGetter,
          columnGroupShow,
          parentColumnId,
          compositionSource,
        }) => ({
          colId,
          ...(valueGetter
            ? { valueGetter }
            : { field: (field ?? colId) as SheetColumn<TRow>["field"] }),
          header: headerName,
          headerName,
          headerComponent: VersionPreviewHeader,
          headerComponentParams: {
            columnType,
            config: column.config ?? null,
            metricKey: column.metric_key,
            title: headerName,
          },
          minWidth: 160,
          flex: column.type === promptTemplateColumnType ? 1.4 : 1,
          ...(columnGroupShow ? { columnGroupShow } : {}),
          cellRenderer: CellRenderer,
          cellRendererParams: {
            columnType,
            tableId,
            sheetId,
            isReadOnly: true,
            openDialog,
            canEditCell: () => false,
            parentColumnId,
            compositionSource,
          },
          context: { columnType },
          enableCellChangeFlash: false,
        }),
        createGroupColumnDef: ({ column, groupId, headerName, columnType, config, children }) => ({
          groupId,
          headerName,
          headerGroupComponent: VersionPreviewGroupHeader,
          headerGroupComponentParams: {
            columnType,
            config: config ?? null,
            title: headerName,
            columnId: column.id,
            columnTitle: column.title,
          },
          marryChildren: true,
          openByDefault: true,
          children,
        }),
      }),
    }),
  );

export const createVersionPreviewColumnDefs = (
  columns: readonly VersionPreviewColumn[],
  openDialog: OpenCellDialog,
  options: VersionPreviewColumnDefOptions = {},
): PreviewColumnDef[] => {
  const { onOpenColumnConfig, selectedColumnId, onOpenColumnAnalytics } = options;
  const baseColumns = buildSmartTableBaseColumnDefs<
    VersionPreviewColumn,
    PreviewLeafColumnDef,
    PreviewGroupColumnDef,
    { data?: VersionPreviewRow }
  >(columns, {
    createLeafColumnDef: context => {
      const sourceColumnId = context.parentColumnId ?? context.colId;
      const sourceColumn = columns.find(column => column.id === sourceColumnId);
      const cellStyle = createPreviewCellStyle(sourceColumn ?? columns[0]!);

      return createPreviewLeafColumn({
        ...context,
        column: sourceColumn ?? columns[0]!,
        openDialog,
        onOpenColumnConfig,
        onOpenColumnAnalytics,
        selectedColumnId,
        cellStyle,
      });
    },
    createGroupColumnDef: ({ groupId, headerName, columnType, config, children }) => {
      const sourceColumn = columns.find(column => column.id === groupId) ?? columns[0]!;

      return {
        groupId,
        headerName,
        marryChildren: true,
        openByDefault: true,
        headerGroupComponent: VersionPreviewGroupHeader,
        headerGroupComponentParams: {
          columnType,
          config: config ?? null,
          title: headerName,
          diffStatus: sourceColumn.diffStatus,
          columnId: sourceColumn.id,
          columnTitle: sourceColumn.title,
          onOpenColumnConfig,
          onOpenColumnAnalytics,
        },
        children,
      };
    },
  });

  return groupConsecutiveInputColumns<VersionPreviewRow>({
    columns: withPreviewGroupHeaders(
      columns,
      nestSmartTableGroupedChildColumns({ columns, columnDefs: baseColumns }),
      sourceColumn => ({
        diffStatus: sourceColumn.diffStatus,
        onOpenColumnConfig,
        onOpenColumnAnalytics,
      }),
    ),
    getColumnType: getPreviewColumnType,
  });
};
